import React, { FC, useEffect, useMemo, useRef, useState } from "react";

import { Icon, Loader } from "@components";
import { useOutsideClick } from "@hooks";
import { useI18n } from "@i18n";
import { IOptionModel, Nullable } from "@utils/types";
import {
  ClearButton,
  ClearButtonWrapper,
  Filter,
  FilterOptionBlock,
  FilterOptionButton,
  FilterOptionWrapper,
  FilterTooltip,
  NoOptions,
  SearchBox,
  SearchInput,
  TooltipWrapper,
  Trigger,
} from "./GenericFilter.styles";

type OptionValue = IOptionModel<string | number>["value"];

export interface GenericFilterType {
  className?: string;
  minTooltipWidth?: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onChange?: (data: OptionValue[]) => void;
  options?: IOptionModel<string | number>[];
  selected?: Nullable<OptionValue[]>;
  enableSearch?: boolean;
  dataLoading?: boolean;
  multiSelect?: boolean;
}

export const GenericFilter: FC<GenericFilterType> = ({
  minTooltipWidth,
  options = [],
  selected,
  onChange,
  enableSearch = true,
  dataLoading = false,
  multiSelect = false,
}) => {
  const { t } = useI18n();

  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<Set<OptionValue>>(
    new Set(selected || [])
  );
  const [search, setSearch] = useState<string>("");
  const rootRef = useRef<HTMLDivElement>(null);

  const showLoading = dataLoading;

  useEffect(() => {
    if (showTooltip) {
      rootRef.current
        ?.getElementsByClassName("selected")[0]
        ?.scrollIntoView(false);
    } else {
      // Reset search when the tooltip is closed
      setSearch("");
    }
  }, [showTooltip]);

  useEffect(() => {
    // Make selected option controllable by the parent component
    if (selected) {
      setSelectedOptions(new Set(selected || []));
    }
  }, [selected]);

  useOutsideClick(rootRef, () => setShowTooltip(false));

  const onClick = () => {
    setShowTooltip((prevState) => !prevState);
  };

  const onOptionSelect = (option: IOptionModel<string | number>) => {
    const currentlySelected = new Set(selectedOptions);
    if (currentlySelected.has(option.value)) {
      currentlySelected.delete(option.value);
    } else {
      if (!multiSelect) {
        currentlySelected.clear();
      }
      currentlySelected.add(option.value);
    }
    setSelectedOptions(currentlySelected);
    onChange?.(Array.from(currentlySelected));
    setShowTooltip(false);
  };

  const clearChoice = () => {
    if (search) {
      setSearch("");
    }
    if (selectedOptions.size) {
      setSelectedOptions(new Set());
      onChange?.([]);
      setShowTooltip(false);
    }
  };

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event?.target?.value || "");
  };

  const filterOptions = useMemo(() => {
    if (!search) return options;

    return options.filter((option) =>
      option.label?.toLowerCase()?.includes(search.toLowerCase())
    );
  }, [search, options]);

  const filterIsNotApplied = selectedOptions.size === 0 && !search;

  return (
    <Filter ref={rootRef} onClick={(e) => e.stopPropagation()}>
      <Trigger onClick={onClick}>
        <Icon.Filter title="Filter column" applied={!filterIsNotApplied} />
      </Trigger>

      <TooltipWrapper>
        {showTooltip && (
          <FilterTooltip style={{ minWidth: minTooltipWidth }}>
            <ClearButtonWrapper>
              <ClearButton onClick={clearChoice}>
                {t("filters.clear")}
              </ClearButton>
            </ClearButtonWrapper>

            {enableSearch && (
              <SearchBox>
                <Icon.Search style={{ height: "100%" }} />
                <SearchInput
                  value={search}
                  name="search"
                  placeholder="Search..."
                  autoComplete="off"
                  spellCheck="false"
                  onInput={onSearch}
                />
              </SearchBox>
            )}

            <FilterOptionBlock hasSearch={enableSearch}>
              {showLoading ? (
                <div>
                  <Loader size="small" />
                </div>
              ) : filterOptions.length === 0 ? (
                <FilterOptionWrapper>
                  <NoOptions>{t("noItems")}</NoOptions>
                </FilterOptionWrapper>
              ) : (
                filterOptions.map((option, index) => (
                  <FilterOptionWrapper key={index} title={option.label}>
                    <FilterOptionButton
                      onClick={() => onOptionSelect(option)}
                      className={
                        selectedOptions.has(option.value) ? "selected" : ""
                      }
                    >
                      {option.label}
                    </FilterOptionButton>
                  </FilterOptionWrapper>
                ))
              )}
            </FilterOptionBlock>
          </FilterTooltip>
        )}
      </TooltipWrapper>
    </Filter>
  );
};

export default GenericFilter;
